import { useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/useEntityOperations';
import { useAuth } from '@/hooks/shared/useAuth';
import { Client, ClientFilters, ClientStats, DbClient } from '@/types/entities/client';
import { Database } from '@/types/supabase';
import { dbClientToClient, clientToDbClient } from '@/utils/transformers/clientTransformers';

// Используем типы, совместимые с Supabase Database схемой
type DbClientInsert = Database['public']['Tables']['clients']['Insert'];
type DbClientUpdate = Database['public']['Tables']['clients']['Update'];

export const useClientData = (filters?: ClientFilters) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['clients', { userId: user?.id, ...filters }];
  
  // Принудительная очистка кэша удалена - проблема была в ClientRepository с select('*')

  const { data: clients, isLoading, error, refetch } = useEntityList<Client, 'clients'> ({
    tableName: 'clients',
    queryKey,
    dbEntityToEntityMapper: dbClientToClient,
    enabled: !!user,
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Явно указываем все поля, включая full_name и company_name
    selectQuery: `
      id,
      user_id,
      created_at,
      updated_at,
      rental_point_id,
      type,
      legal_entity_type,
      full_name,
      company_name,
      inn,
      kpp,
      phone,
      email,
      address,
      birth_date,
      passport_series,
      passport_number,
      passport_issued_by,
      passport_issue_date,
      bank_name,
      bank_account,
      bank_bik,
      contacts,
      comment,
      loyalty_status,
      status,
      registration_date,
      remind_birthday,
      remind_days_before
    `
  });

  const {
    createEntity,
    updateEntity,
    deleteEntity,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEntityOperations<Client, 'clients'> ({
    tableName: 'clients',
    queryKey,
    entityToInsertDbMapper: (client) => {
      const dbClient = clientToDbClient(client as Client);
      // Исключаем только системные поля id, created_at, updated_at
      const { id, created_at, updated_at, ...insertData } = dbClient;
      return insertData as unknown as DbClientInsert;
    },
    entityToUpdateDbMapper: (client) => {
      // Проверяем, что client существует
      if (!client) {
        console.error('❌ [useClientData] entityToUpdateDbMapper: client is undefined or null');
        return {};
      }
      
      // Создаем маппер для частичного обновления, аналогичный updateClientInputToDb
      const partialClient = client as Partial<Client>;
      console.log('🔧 [useClientData] entityToUpdateDbMapper input:', partialClient);
      const result: Partial<DbClientUpdate> = {};
      
      // Преобразуем только те поля, которые присутствуют в partialClient
      if (partialClient.type !== undefined) result.type = partialClient.type;
      if (partialClient.legalEntityType !== undefined) result.legal_entity_type = partialClient.legalEntityType;
      if (partialClient.fullName !== undefined) result.full_name = partialClient.fullName;
      if (partialClient.companyName !== undefined) result.company_name = partialClient.companyName;
      if (partialClient.inn !== undefined) result.inn = partialClient.inn;
      if (partialClient.kpp !== undefined) result.kpp = partialClient.kpp;
      if (partialClient.phone !== undefined) result.phone = partialClient.phone;
      if (partialClient.email !== undefined) result.email = partialClient.email;
      if (partialClient.address !== undefined) result.address = partialClient.address;
      if (partialClient.birthDate !== undefined) result.birth_date = partialClient.birthDate ? partialClient.birthDate.toISOString() : null;
      if (partialClient.passportSeries !== undefined) result.passport_series = partialClient.passportSeries;
      if (partialClient.passportNumber !== undefined) result.passport_number = partialClient.passportNumber;
      if (partialClient.passportIssuedBy !== undefined) result.passport_issued_by = partialClient.passportIssuedBy;
      if (partialClient.passportIssueDate !== undefined) result.passport_issue_date = partialClient.passportIssueDate ? partialClient.passportIssueDate.toISOString() : null;
      if (partialClient.bankName !== undefined) result.bank_name = partialClient.bankName;
      if (partialClient.bankAccount !== undefined) result.bank_account = partialClient.bankAccount;
      if (partialClient.bankBik !== undefined) result.bank_bik = partialClient.bankBik;
      if (partialClient.comment !== undefined) result.comment = partialClient.comment;
      if (partialClient.loyaltyStatus !== undefined) result.loyalty_status = partialClient.loyaltyStatus;
      if (partialClient.status !== undefined) result.status = partialClient.status;
      if (partialClient.remindBirthday !== undefined) result.remind_birthday = partialClient.remindBirthday;
      if (partialClient.remindDaysBefore !== undefined) result.remind_days_before = partialClient.remindDaysBefore;
      
      // Всегда обновляем updated_at
      result.updated_at = new Date().toISOString();
      
      console.log('✅ [useClientData] entityToUpdateDbMapper result:', result);
      return result as unknown as DbClientUpdate;
    },
    dbEntityToEntityMapper: (dbEntity) => {
      // Преобразуем Database тип в наш DbClient тип для корректного маппинга
      return dbClientToClient(dbEntity as unknown as DbClient);
    },
  });

  return {
    clients,
    isLoading,
    error,
    refetch,
    createClient: createEntity,
    updateClient: updateEntity,
    deleteClient: deleteEntity,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
